import React, { useImperativeHandle } from 'react';
import { DropTarget } from 'react-dnd';
import { mapItemTypeToBaseFormElement } from '../defaults/baseFormElements';

function getElement(item, props) {
  const Element = mapItemTypeToBaseFormElement[item?.type];
  return Element ? <Element {...props} key={`form_${item.id}`} data={item} /> : null;
}

function getStyle(backgroundColor) {
  return {
    border: '1px solid rgba(0,0,0,0.2)',
    minHeight: 75,
    // height: '100%',
    minWidth: '7rem',
    width: '100%',
    backgroundColor,
    padding: 0,
    float: 'left',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };
}

const Dustbin = React.forwardRef(
  (
    {
      styles,
      onDropSuccess,
      seq,
      draggedItem,
      parentIndex,
      canDrop,
      isOver,
      isOverCurrent,
      connectDropTarget,
      items,
      col,
      getDataById,
      ...rest
    },
    ref,
  ) => {
    const item = getDataById(items[col]);

    useImperativeHandle(
      ref,
      () => ({
        onDrop: dropped => {
          const { data } = dropped;
          if (data) {
            onDropSuccess && onDropSuccess(dropped);
          }
        },
      }),
      [],
    );

    const element = getElement(item, rest);

    const sameCard = draggedItem ? draggedItem.index === parentIndex : false;

    let backgroundColor = 'rgba(0, 0, 0, .03)';

    if (!sameCard && isOver && canDrop && !draggedItem.data.isContainer) {
      backgroundColor = '#F7F589';
    }

    return connectDropTarget(
      <div style={!sameCard ? getStyle(backgroundColor) : getStyle('rgba(0, 0, 0, .03')}>
        {!element && <span>Drop your element here </span>}
        {element}
      </div>,
    );
  },
);

export default DropTarget(
  props => props.accepts,
  {
    drop(props, monitor, component) {
      const {
        getDataById,
        onUpdateMultipleElements,
        onBulkManipulation,
        onAddItem,
        disableCreation = false,
        onDropSuccess,
      } = props;
      const item = monitor.getItem();

      onDropSuccess && onDropSuccess(item);

      if (!component || disableCreation) return;

      const isBusy = !!props.items[props.col];
      const container = props.data;

      if (isBusy || item.data.isContainer) return;

      const isNewItem = !item.data.id;

      if (isNewItem && onAddItem) {
        const createdItem = item.onCreate(item.data);

        onBulkManipulation({
          itemsToAdd: [{ ...createdItem, parentId: props.data.id }],
          itemsToUpdate: [
            {
              ...container,
              childItems: container.childItems.with(props.col, createdItem.id),
            },
          ],
        });
      } else {
        const itemParentId = item.data.parentId;
        const isSameParent = itemParentId === container.id;

        const newItem = { ...item.data, parentId: container.id };
        const newParent = {
          ...container,
          childItems:
            itemParentId && isSameParent
              ? container.childItems
                  .map(id => (id === item.data.id ? null : id))
                  .with(props.col, item.data.id)
              : container.childItems.with(props.col, item.data.id),
        };

        const elementsToUpdate = [newItem, newParent];

        if (!isSameParent && itemParentId) {
          const parent = getDataById(itemParentId);

          elementsToUpdate.push({
            ...parent,
            childItems: parent.childItems.map(id => (id === item.data.id ? null : id)),
          });
        }

        onUpdateMultipleElements(elementsToUpdate);
      }
    },
  },
  (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    draggedItem: monitor.getItem() ? monitor.getItem() : null,
    isOver: monitor.isOver(),
    isOverCurrent: monitor.isOver({ shallow: true }),
    canDrop: monitor.canDrop(),
  }),
)(Dustbin);
